import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import type { FiltersGroup } from '@sorare/core/src/lib/filters';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { SORTS } from '@sorare/marketplace/src/searchCards/football/sorts';
import {
  BackendCompetitionEligibilityGroup,
  scoutingCenterBackendMarketFilters,
} from '@sorare/marketplace/src/searchCommonPlayer/filtersGroup/football';

import { ScoutingCenterHeader } from '#src/components/scoutingCenter/ScoutingCenterHeader/index.tsx';
import { FootballPlayerSearch } from '#src/components/searchCards/FootballPlayerSearch/index.tsx';

import type {
  FootballScoutingCenter,
  FootballScoutingCenterVariables,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  min-height: calc(100vh - var(--current-stack-height));
`;

const FOOTBALL_SCOUTING_CENTER_QUERY = gql`
  query FootballScoutingCenter(
    $slug: String!
    $withSlug: Boolean!
    $sport: Sport!
  ) {
    so5 {
      so5Competition(slug: $slug) @include(if: $withSlug) {
        slug
        algoliaFilters
        scoutable
        competitions {
          slug
          ...ScoutingCenterHeader_competition
        }
        ...ScoutingCenterHeader_so5Competition
        playableSo5LeaderboardGroupInterfaces {
          slug
          so5Fixture {
            slug
          }
        }
      }
      so5Fixture(type: UPCOMING, eventType: CLASSIC, sport: $sport) {
        id
        slug
      }
    }
  }
  ${ScoutingCenterHeader.fragments.so5Competition}
  ${ScoutingCenterHeader.fragments.competition}
` as TypedDocumentNode<FootballScoutingCenter, FootballScoutingCenterVariables>;

export const ScoutingCenter = ({ slug }: { slug?: string }) => {
  const { data, loading } = useQuery(FOOTBALL_SCOUTING_CENTER_QUERY, {
    variables: {
      slug: slug || '',
      withSlug: !!slug,
      sport: Sport.FOOTBALL,
    },
  });

  const { so5Competition, so5Fixture } = data?.so5 || {};

  const playableSo5LeaderboardGroupInterfaces =
    so5Competition?.playableSo5LeaderboardGroupInterfaces;

  const so5FixtureSlug =
    playableSo5LeaderboardGroupInterfaces?.[0]?.so5Fixture?.slug ||
    so5Fixture?.slug ||
    '';

  const cardFilters: FiltersGroup[] = [
    ...(!slug ? [BackendCompetitionEligibilityGroup] : []),
    ...scoutingCenterBackendMarketFilters,
  ];

  const defaultFilters = useDefaultFilters({
    extraFilters: so5Competition?.algoliaFilters || undefined,
  });

  return (
    <Wrapper>
      <ScoutingCenterHeader
        so5Competition={so5Competition}
        competitions={so5Competition?.competitions}
        loading={loading}
      />
      <div>
        <FootballPlayerSearch
          key={so5Competition?.slug || 'DEFAULT'}
          sorts={SORTS}
          defaultSort={SORTS[0]}
          cardFilters={cardFilters}
          defaultFilters={defaultFilters}
          analyticsTags={['Scouting-center', 'Football']}
          topic={{ type: 'scoutingCenter', label: '' }}
          attributesToRetrieve={['slug']}
          so5FixtureSlug={so5FixtureSlug}
          shouldSkip={loading}
        />
      </div>
    </Wrapper>
  );
};
